/* eslint-disable */
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  MenuItem,
  Typography,
  CircularProgress,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  AttachMoney as MoneyIcon,
  Calculate as CalculateIcon,
  Percent as PercentIcon,
  Receipt as TaxIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountBalanceIcon,
  CarRental as CarIcon,
  LocalPhone as PhoneIcon,
  DirectionsCar as DriverIcon,
  Build as BuildIcon,
  Flight as FlightIcon,
} from "@mui/icons-material";
import { processMonthlyPayroll } from "store/features/company/payrollManagement/processPayroll/processPayrollActions";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import { selectEmployees } from "store/features/company/employee/employeeSelectors";
import { fetchSalaryConfigurations } from "store/features/company/payrollManagement/salaryConfiguration/salaryConfigurationActions";
import { selectSalaryConfigurations } from "store/features/company/payrollManagement/salaryConfiguration/salaryConfigurationSelectors";
import addPayrollSvg from "assets/modals/add.svg";

const AddProcessPayrollModal = ({
  isOpen,
  onClose,
  onSuccess,
  month,
  year,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const employees = useSelector(selectEmployees);
  const salaryConfigurations = useSelector(selectSalaryConfigurations);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchEmployees());
      dispatch(fetchSalaryConfigurations());
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen, dispatch]);

  const [loading, setLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const employeeRefs = useRef([]);

  const [modalMonth, setModalMonth] = useState(
    month || new Date().getMonth() + 1,
  );
  const [modalYear, setModalYear] = useState(year || new Date().getFullYear());

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - 2 + i,
  );

  const findEmployeeSalaryConfig = (employeeId) => {
    if (!employeeId || !salaryConfigurations.length) return null;
    return salaryConfigurations.find(
      (config) =>
        config.employee_id === employeeId && config.status === "active",
    );
  };

  const handleAddEmployee = () => {
    const newIndex = selectedEmployees.length;
    setSelectedEmployees([
      ...selectedEmployees,
      {
        id: "",
        employee_id: "",
        basic_salary: "",
        hra: "",
        conveyance: "",
        education_allowance: "",
        transport_allowance: "",
        fixed_allowance: "",
        bonus: "",
        overtime_amount: "",
        telephone_reimbursement: "",
        provident_fund: "",
        professional_tax: "",
        tax_amount: "",
      },
    ]);

    // Scroll to the new employee card after it's rendered
    setTimeout(() => {
      if (employeeRefs.current[newIndex]) {
        employeeRefs.current[newIndex].scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  const handleRemoveEmployee = (index) => {
    const newList = selectedEmployees.filter((_, i) => i !== index);
    setSelectedEmployees(newList);
  };

  const handleEmployeeChange = async (index, field, value) => {
    const newList = [...selectedEmployees];
    newList[index][field] = value;

    if (field === "id" && value) {
      const employeeId = parseInt(value);
      const selectedEmp = employees.find((e) => e.id === employeeId);
      const salaryConfig = findEmployeeSalaryConfig(employeeId);

      if (selectedEmp && salaryConfig) {
        newList[index].employee_id = employeeId;
        newList[index].basic_salary = salaryConfig.custom_basic_salary || "";
        newList[index].hra = salaryConfig.custom_hra_amount || "";
        newList[index].conveyance = salaryConfig.custom_conveyance_amount || "";
        newList[index].education_allowance =
          salaryConfig.custom_education_allowance_amount || "";
        newList[index].transport_allowance =
          salaryConfig.custom_transport_allowance_amount || "";
        newList[index].fixed_allowance =
          salaryConfig.custom_fixed_allowance_amount || "";
        newList[index].telephone_reimbursement =
          salaryConfig.custom_telephone_reimbursement || "";

        const pfAmount =
          (parseFloat(salaryConfig.custom_basic_salary) || 0) * 0.12;
        newList[index].provident_fund = pfAmount;
        newList[index].professional_tax = "200";
      } else if (selectedEmp) {
        newList[index].employee_id = employeeId;
      }
    }

    setSelectedEmployees(newList);
  };

  const calculateTotalEarnings = (emp) => {
    return (
      (parseFloat(emp.basic_salary) || 0) +
      (parseFloat(emp.hra) || 0) +
      (parseFloat(emp.conveyance) || 0) +
      (parseFloat(emp.education_allowance) || 0) +
      (parseFloat(emp.transport_allowance) || 0) +
      (parseFloat(emp.fixed_allowance) || 0) +
      (parseFloat(emp.bonus) || 0) +
      (parseFloat(emp.overtime_amount) || 0) +
      (parseFloat(emp.telephone_reimbursement) || 0)
    );
  };

  const calculateTotalDeductions = (emp) => {
    return (
      (parseFloat(emp.provident_fund) || 0) +
      (parseFloat(emp.professional_tax) || 0) +
      (parseFloat(emp.tax_amount) || 0)
    );
  };

  const calculateNetPay = (emp) => {
    return calculateTotalEarnings(emp) - calculateTotalDeductions(emp);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const employeesData = selectedEmployees.map((emp) => ({
      employee_id: parseInt(emp.employee_id),
      basic_salary: parseFloat(emp.basic_salary) || 0,
      hra: parseFloat(emp.hra) || 0,
      conveyance: parseFloat(emp.conveyance) || 0,
      education_allowance: parseFloat(emp.education_allowance) || 0,
      transport_allowance: parseFloat(emp.transport_allowance) || 0,
      fixed_allowance: parseFloat(emp.fixed_allowance) || 0,
      bonus: parseFloat(emp.bonus) || 0,
      overtime_amount: parseFloat(emp.overtime_amount) || 0,
      telephone_reimbursement: parseFloat(emp.telephone_reimbursement) || 0,
      provident_fund: parseFloat(emp.provident_fund) || 0,
      professional_tax: parseFloat(emp.professional_tax) || 0,
      tax_amount: parseFloat(emp.tax_amount) || 0,
    }));

    const payrollData = {
      month: modalMonth,
      year: modalYear,
      employees_data: employeesData,
    };

    const result = await dispatch(processMonthlyPayroll(payrollData));
    if (result.success) {
      onSuccess();
      onClose();
      setSelectedEmployees([]);
    }
    setLoading(false);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#f59e0b",
    },
  };

  const getMonthName = (m) => {
    return months[m - 1];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <style>
            {`
              .MuiPopover-root { z-index: 1400 !important; }
              .MuiPopper-root { z-index: 1400 !important; }
              .MuiMenu-root { z-index: 1400 !important; }
            `}
          </style>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "700px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={addPayrollSvg}
                      alt="Process Payroll"
                      sx={{ width: 80, height: 80, objectFit: "contain" }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                      }}
                    >
                      Process Payroll
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      Process payroll for employees for a specific month
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                  {/* Payroll Period Selection */}
                  <Accordion
                    defaultExpanded
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <AccountBalanceIcon sx={{ color: "#f59e0b" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Payroll Period
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            select
                            label="Month"
                            value={modalMonth}
                            onChange={(e) =>
                              setModalMonth(parseInt(e.target.value))
                            }
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          >
                            {months.map((m, idx) => (
                              <MenuItem key={idx} value={idx + 1}>
                                {m}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            fullWidth
                            select
                            label="Year"
                            value={modalYear}
                            onChange={(e) =>
                              setModalYear(parseInt(e.target.value))
                            }
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          >
                            {years.map((y) => (
                              <MenuItem key={y} value={y}>
                                {y}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  {/* Empty State */}
                  {selectedEmployees.length === 0 && (
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: "center",
                        borderRadius: "16px",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                        border: `1px solid ${
                          theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                        }`,
                      }}
                    >
                      <MoneyIcon
                        sx={{ fontSize: 48, color: "#9ca3af", mb: 2 }}
                      />
                      <Typography sx={{ color: "#6b7280", mb: 1 }}>
                        No employees added yet
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                        Click the "Add Employee" button to start processing
                        payroll
                      </Typography>
                    </Paper>
                  )}

                  {/* Employee Cards */}
                  {selectedEmployees.map((emp, idx) => (
                    <Accordion
                      key={idx}
                      ref={(el) => (employeeRefs.current[idx] = el)}
                      defaultExpanded
                      sx={{
                        borderRadius: "16px !important",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                        boxShadow: "none",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          "& .MuiAccordionSummary-content": {
                            alignItems: "center",
                            justifyContent: "space-between",
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          flex={1}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Employee {idx + 1}
                          </Typography>
                          {emp.employee_id && (
                            <Chip
                              label={
                                employees.find(
                                  (e) => e.id === parseInt(emp.employee_id),
                                )?.first_name || "Selected"
                              }
                              size="small"
                              sx={{
                                backgroundColor: "#fef3c7",
                                color: "#f59e0b",
                                fontWeight: 500,
                              }}
                            />
                          )}
                        </Stack>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveEmployee(idx);
                          }}
                          size="small"
                          sx={{ color: "#ef4444", mr: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </AccordionSummary>

                      <AccordionDetails>
                        <Stack spacing={3} sx={{ width: "100%" }}>
                          {/* Employee Selection */}
                          <TextField
                            select
                            label="Select Employee"
                            fullWidth
                            value={emp.id}
                            onChange={(e) =>
                              handleEmployeeChange(idx, "id", e.target.value)
                            }
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          >
                            <MenuItem value="">Choose an employee...</MenuItem>
                            {employees.map((employee) => (
                              <MenuItem key={employee.id} value={employee.id}>
                                {employee.first_name} {employee.last_name} (
                                {employee.employee_code}) -{" "}
                                {employee.department_name}
                              </MenuItem>
                            ))}
                          </TextField>

                          {/* Earnings Section */}
                          <Accordion
                            defaultExpanded
                            sx={{
                              borderRadius: "12px !important",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(30,41,59,0.6)"
                                  : "#ffffff",
                              boxShadow: "none",
                              border: `1px solid ${
                                theme.palette.mode === "dark"
                                  ? "#334155"
                                  : "#e5e7eb"
                              }`,
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                "& .MuiAccordionSummary-content": {
                                  alignItems: "center",
                                  gap: 1,
                                },
                              }}
                            >
                              <MoneyIcon sx={{ color: "#f59e0b" }} />
                              <Typography sx={{ fontWeight: 600 }}>
                                Earnings
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={12} md={12}>
                                  <TextField
                                    label="Basic Salary"
                                    type="number"
                                    fullWidth
                                    value={emp.basic_salary}
                                    onChange={(e) =>
                                      handleEmployeeChange(
                                        idx,
                                        "basic_salary",
                                        e.target.value,
                                      )
                                    }
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                  <TextField
                                    label="HRA"
                                    type="number"
                                    fullWidth
                                    value={emp.hra}
                                    onChange={(e) =>
                                      handleEmployeeChange(
                                        idx,
                                        "hra",
                                        e.target.value,
                                      )
                                    }
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                  <TextField
                                    label="Conveyance Allowance"
                                    type="number"
                                    fullWidth
                                    value={emp.conveyance}
                                    onChange={(e) =>
                                      handleEmployeeChange(
                                        idx,
                                        "conveyance",
                                        e.target.value,
                                      )
                                    }
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                  <TextField
                                    label="Education Allowance"
                                    type="number"
                                    fullWidth
                                    value={emp.education_allowance}
                                    onChange={(e) =>
                                      handleEmployeeChange(
                                        idx,
                                        "education_allowance",
                                        e.target.value,
                                      )
                                    }
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                  <TextField
                                    label="Transport Allowance"
                                    type="number"
                                    fullWidth
                                    value={emp.transport_allowance}
                                    onChange={(e) =>
                                      handleEmployeeChange(
                                        idx,
                                        "transport_allowance",
                                        e.target.value,
                                      )
                                    }
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                  <TextField
                                    label="Fixed Allowance"
                                    type="number"
                                    fullWidth
                                    value={emp.fixed_allowance}
                                    onChange={(e) =>
                                      handleEmployeeChange(
                                        idx,
                                        "fixed_allowance",
                                        e.target.value,
                                      )
                                    }
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                  <TextField
                                    label="Bonus"
                                    type="number"
                                    fullWidth
                                    value={emp.bonus}
                                    onChange={(e) =>
                                      handleEmployeeChange(
                                        idx,
                                        "bonus",
                                        e.target.value,
                                      )
                                    }
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                  <TextField
                                    label="Overtime Amount"
                                    type="number"
                                    fullWidth
                                    value={emp.overtime_amount}
                                    onChange={(e) =>
                                      handleEmployeeChange(
                                        idx,
                                        "overtime_amount",
                                        e.target.value,
                                      )
                                    }
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                  <TextField
                                    label="Telephone Reimbursement"
                                    type="number"
                                    fullWidth
                                    value={emp.telephone_reimbursement}
                                    onChange={(e) =>
                                      handleEmployeeChange(
                                        idx,
                                        "telephone_reimbursement",
                                        e.target.value,
                                      )
                                    }
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>

                          {/* Deductions Section */}
                          <Accordion
                            defaultExpanded
                            sx={{
                              borderRadius: "12px !important",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(30,41,59,0.6)"
                                  : "#ffffff",
                              boxShadow: "none",
                              border: `1px solid ${
                                theme.palette.mode === "dark"
                                  ? "#334155"
                                  : "#e5e7eb"
                              }`,
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                "& .MuiAccordionSummary-content": {
                                  alignItems: "center",
                                  gap: 1,
                                },
                              }}
                            >
                              <PercentIcon sx={{ color: "#f59e0b" }} />
                              <Typography sx={{ fontWeight: 600 }}>
                                Deductions & Taxes
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={12} md={12}>
                                  <TextField
                                    label="Provident Fund (12% of Basic)"
                                    type="number"
                                    fullWidth
                                    value={emp.provident_fund}
                                    onChange={(e) =>
                                      handleEmployeeChange(
                                        idx,
                                        "provident_fund",
                                        e.target.value,
                                      )
                                    }
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                  <TextField
                                    label="Professional Tax"
                                    type="number"
                                    fullWidth
                                    value={emp.professional_tax}
                                    onChange={(e) =>
                                      handleEmployeeChange(
                                        idx,
                                        "professional_tax",
                                        e.target.value,
                                      )
                                    }
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                  <TextField
                                    label="Tax Amount (TDS)"
                                    type="number"
                                    fullWidth
                                    value={emp.tax_amount}
                                    onChange={(e) =>
                                      handleEmployeeChange(
                                        idx,
                                        "tax_amount",
                                        e.target.value,
                                      )
                                    }
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>

                          {/* Salary Summary */}
                          {emp.basic_salary && (
                            <Paper
                              sx={{
                                p: 2,
                                borderRadius: "12px",
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(245,158,11,0.1)"
                                    : "#fefce8",
                                border: `1px solid ${
                                  theme.palette.mode === "dark"
                                    ? "#f59e0b30"
                                    : "#fef3c7"
                                }`,
                              }}
                            >
                              <Stack spacing={1.5}>
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  alignItems="center"
                                >
                                  <CalculateIcon sx={{ color: "#f59e0b" }} />
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, color: "#f59e0b" }}
                                  >
                                    Salary Summary
                                  </Typography>
                                </Stack>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color:
                                        theme.palette.mode === "dark"
                                          ? "rgb(224, 207, 175)"
                                          : "#484843",
                                    }}
                                  >
                                    Total Earnings:
                                  </Typography>
                                  <Typography
                                    sx={{ fontWeight: 600, color: "#10b981" }}
                                  >
                                    ₹
                                    {calculateTotalEarnings(emp).toLocaleString(
                                      "en-IN",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      },
                                    )}
                                  </Typography>
                                </Stack>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color:
                                        theme.palette.mode === "dark"
                                          ? "rgb(224, 207, 175)"
                                          : "#484843",
                                    }}
                                  >
                                    Total Deductions:
                                  </Typography>
                                  <Typography
                                    sx={{ fontWeight: 600, color: "#ef4444" }}
                                  >
                                    ₹
                                    {calculateTotalDeductions(
                                      emp,
                                    ).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </Typography>
                                </Stack>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  sx={{
                                    pt: 1,
                                    borderTop: `1px dashed ${
                                      theme.palette.mode === "dark"
                                        ? "#334155"
                                        : "#e5e7eb"
                                    }`,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    Net Pay:
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontWeight: 800,
                                      fontSize: "1.1rem",
                                      color: "#f59e0b",
                                    }}
                                  >
                                    ₹
                                    {calculateNetPay(emp).toLocaleString(
                                      "en-IN",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      },
                                    )}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Paper>
                          )}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                  {/* Add Employee Button */}
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={handleAddEmployee}
                    sx={{
                      borderRadius: "12px",
                      px: 3,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      alignSelf: "flex-start",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      },
                    }}
                  >
                    Add Employee
                  </Button>
                </Stack>
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                  display: "flex",
                  gap: 2,
                  justifyContent: "space-between",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                  zIndex: 10,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  variant="contained"
                  sx={{
                    borderRadius: "12px",
                    px: 4,
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    minWidth: "150px",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                    color:
                      theme.palette.mode === "dark" ? "#ffffff" : "#374151",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || selectedEmployees.length === 0}
                  variant="contained"
                  sx={{
                    borderRadius: "12px",
                    px: 4,
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: "250px",
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    `Process Payroll for ${getMonthName(
                      modalMonth,
                    )} ${modalYear}`
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddProcessPayrollModal;
